import { HandleSSOCallback, useAuth } from "@clerk/react";
import { errorCodeOf } from "@luckymenu/api-client";
import { useTranslation } from "@luckymenu/i18n/react";
import {
	type NavigationModel,
	useNavigationModel,
	useRestaurantSession,
} from "@luckymenu/staff-core";
import { AppShell } from "@luckymenu/ui-web";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { type ReactNode, useEffect } from "react";
import { useQuickBillKey } from "./bill/shortcut";
import { Brand } from "./brand";
import { LiveBanners, LiveConnection } from "./live/live-banners";
import { activeTab, tabItems, tabRedirect } from "./nav";
import { RouterLink } from "./router-link";
import { rememberReturnTo, takeReturnTo } from "./session/return-to";
import {
	ChooserScreen,
	NoRestaurantScreen,
	SessionErrorScreen,
	SuspendedScreen,
} from "./session/screens";
import { SupportBanner, SupportEndedScreen, useSupportLink } from "./session/support";
import { SignInScreen } from "./sign-in";

function Loading() {
	const { t } = useTranslation();
	return (
		<main className="flex min-h-dvh items-center justify-center bg-surface">
			<p role="status" className="text-sm text-muted-foreground">
				{t("loading")}
			</p>
		</main>
	);
}

/** Where Google sends the browser back. Clerk finishes the sign-in or sign-up here. */
export function SsoCallback() {
	const navigate = useNavigate();
	return (
		<>
			<Loading />
			<HandleSSOCallback
				navigateToApp={() => void navigate({ to: takeReturnTo() ?? "/", replace: true })}
				navigateToSignIn={() => void navigate({ to: "/", replace: true })}
				navigateToSignUp={() => void navigate({ to: "/", replace: true })}
			/>
		</>
	);
}

function StaffShell({ model, children }: { model: NavigationModel; children: ReactNode }) {
	const { t } = useTranslation("nav");
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const navigate = useNavigate();
	const redirect = tabRedirect(model, pathname);
	useQuickBillKey();
	useEffect(() => {
		if (redirect) void navigate({ to: redirect, replace: true });
	}, [redirect, navigate]);
	return (
		<AppShell
			items={tabItems(model, t)}
			activeKey={activeTab(pathname)}
			navLabel={t("mainNavigation")}
			brand={<Brand compact />}
			linkComponent={RouterLink}
			variant="rail"
			mobileNav="tabs"
		>
			<SupportBanner />
			<LiveBanners />
			{children}
		</AppShell>
	);
}

/** Signed in: which restaurant, then the shell. */
function RestaurantGate({ children }: { children: ReactNode }) {
	const { t } = useTranslation(["staff", "errors"]);
	const session = useRestaurantSession();
	switch (session.status) {
		case "loading":
			return <Loading />;
		case "error":
			return (
				<SessionErrorScreen
					message={
						errorCodeOf(session.error) === "NETWORK" ? t("errors:NETWORK") : t("session.loadFailed")
					}
					onRetry={session.retry}
				/>
			);
		case "none":
			return <NoRestaurantScreen />;
		case "choose":
			return <ChooserScreen restaurants={session.restaurants} />;
		case "supportEnded":
			return <SupportEndedScreen />;
		case "ready":
			// Live while paused too: the screen hears the restaurant come back.
			return (
				<LiveConnection>
					{session.current.restaurant.status === "suspended" ? (
						<SuspendedScreen
							restaurant={session.current.restaurant.name}
							canSwitch={session.restaurants.length > 1}
							ownerBilling={session.current.permissions.includes("subscription.view")}
						/>
					) : (
						<ReadyShell>{children}</ReadyShell>
					)}
				</LiveConnection>
			);
	}
}

/** The shell with the tabs the role may use. */
function ReadyShell({ children }: { children: ReactNode }) {
	const model = useNavigationModel();
	if (!model) return <Loading />;
	return <StaffShell model={model}>{children}</StaffShell>;
}

/**
 * Signed out: sign-in, remembering a join link to come back to. Signed in:
 * join pages stand alone; everything else needs a restaurant. The API decides
 * what data is shown.
 */
export function StaffGate({ children }: { children: ReactNode }) {
	const { isLoaded, isSignedIn } = useAuth();
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const joining = pathname === "/join" || pathname.startsWith("/join/");
	useSupportLink(pathname, Boolean(isSignedIn));
	useEffect(() => {
		if (isLoaded && !isSignedIn && joining) rememberReturnTo(pathname);
	}, [isLoaded, isSignedIn, joining, pathname]);
	if (!isLoaded) return <Loading />;
	if (!isSignedIn) return <SignInScreen />;
	if (joining) return <>{children}</>;
	// Starting a support session: `useSupportLink` moves on to Orders.
	if (pathname === "/support") return <Loading />;
	return <RestaurantGate>{children}</RestaurantGate>;
}
